import { Box, Stack } from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    EntryResolvedReason,
    PayStationPaymentInfoState,
    PayStationPaymentsInfo,
} from './CloudConnectorPeripheriesOverview.tsx';
import { RequestStatus } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import {
    CloudConnectorPeripheryEventType,
    CloudConnectorPeripheryType,
    SpecialVehicleType,
} from 'dg-web-shared/model/CloudConnector.ts';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { BarrierGateIdentificationString } from '../offstreet-transactions/OperatorOffstreetTransactionsRoute.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { ParkingPortalButton } from '../mobile/layout/ParkingPortalButton.tsx';
import {
    DirectionsBus,
    KeyboardControlKey,
    KeyboardDoubleArrowRight,
    TwoWheeler,
} from '@mui/icons-material';
import { usePeripheryDetailsOutlet } from './CloudConnectorPeripheriesDetail.tsx';
import {
    CloudConnectorCommandType,
    isPeripheryDisconnected,
    RequestErrorAlert,
    usePeripheryCommand,
} from './CloudConnectorPeripheryDetailTabs.tsx';
import { Navigate } from 'react-router-dom';
import { IdentificationMedia } from 'dg-web-shared/model/IdentificationMedia.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { PaymentChannel } from '../common/models/PaymentChannel.ts';
import { type JSX, ReactNode } from 'react';
import { PaymentChannelLocalized } from '../common/components/PaymentChannelLocalized.tsx';

export function PeripheryPresenceTab() {
    const { currentLogin } = useOperatorContext();
    const { peripheryConfig } = usePeripheryDetailsOutlet();
    const isDisconnected = isPeripheryDisconnected(peripheryConfig);
    const [peripheryCommandRequestState, executePeripheryCommand] =
        usePeripheryCommand();
    const barrierPermanentlyOpen = peripheryConfig.events.some(
        event =>
            event.eventType ===
            CloudConnectorPeripheryEventType.BARRIER_PERMANENTLY_OPEN,
    );
    const presence = peripheryConfig.presence;
    const identification = presence?.identification;
    const runningEntryWithoutExit = presence?.runningEntryWithoutExit;
    const logInfo = presence?.logInfo;
    const isExitPeriphery =
        peripheryConfig.peripheryType === CloudConnectorPeripheryType.EXIT;

    if (isDisconnected || peripheryConfig.withoutPresence) {
        return <Navigate to={'../detail'} />;
    }

    return (
        <Stack
            direction={'column'}
            sx={{
                height: '100%',
                display: 'flex',
                flexGrow: 1,
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    padding: theme => theme.spacing(3),
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            color: theme => theme.palette.primary.main,
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: 'center',
                                textTransform: 'uppercase',
                            }}
                        >
                            {presence ? (
                                <Box>
                                    <b>
                                        <Localized
                                            de="Fahrzeug anwesend"
                                            fr="Véhicule présent"
                                            it="Veicolo presente"
                                            en="Vehicle present"
                                        />
                                    </b>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: '28px',
                                        }}
                                    >
                                        {presence.specialVehicle && (
                                            <SpecialVehicleIcon
                                                specialVehicleType={
                                                    presence.specialVehicle
                                                }
                                            />
                                        )}

                                        <Box sx={{ paddingLeft: '5px' }}>
                                            {presence.lprReadLicensePlate}
                                        </Box>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        color: theme => theme.palette.grey.A400,
                                    }}
                                >
                                    <Localized
                                        de="Kein Fahrzeug"
                                        fr="Aucun véhicule"
                                        it="Nessun veicolo"
                                        en="No vehicle"
                                    />
                                    <Box sx={{ minHeight: '28px' }} />
                                </Box>
                            )}
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: 'row',
                                paddingTop: theme => theme.spacing(3),
                            }}
                        >
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Identifikation"
                                        fr="Identification"
                                        it="Identificazione"
                                        en="Identification"
                                    />
                                }
                                value={
                                    identification ? (
                                        <BarrierGateIdentificationString
                                            identificationType={
                                                identification.identificationMedia
                                            }
                                            identificationString={
                                                identification.identificationString
                                            }
                                        />
                                    ) : (
                                        EmDash
                                    )
                                }
                            />

                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Meldung"
                                        fr="Message"
                                        it="Messaggio"
                                        en="Message"
                                    />
                                }
                                value={
                                    !logInfo ? (
                                        EmDash
                                    ) : logInfo.errorTranslation ? (
                                        <Localized
                                            {...logInfo.errorTranslation}
                                        />
                                    ) : logInfo.isSuccess ? (
                                        <PresenceOkMessage
                                            peripheryType={
                                                peripheryConfig.peripheryType
                                            }
                                        />
                                    ) : (
                                        <PresenceNotOkMessage
                                            peripheryType={
                                                peripheryConfig.peripheryType
                                            }
                                        />
                                    )
                                }
                                sx={{
                                    textTransform: 'uppercase',
                                    color: theme =>
                                        !logInfo
                                            ? theme.palette.primary.main
                                            : logInfo.isSuccess
                                              ? theme.palette.success.main
                                              : theme.palette.error.main,
                                }}
                            />
                        </Box>

                        {(runningEntryWithoutExit ||
                            (presence?.resolvedEntryWithoutExits.length ?? 0) >
                                0) && (
                            <PeripheryDetailSeparator noMarginBottom />
                        )}

                        <Box>
                            {runningEntryWithoutExit && (
                                <Box
                                    sx={{
                                        paddingTop: theme => theme.spacing(2),
                                        color: theme =>
                                            theme.palette.primary.main,
                                    }}
                                >
                                    <PeripheryDetailTitle>
                                        <Localized
                                            de="Laufende Transaktion"
                                            fr="Transaction en cours"
                                            it="Transazione attuale"
                                            en="Current transaction"
                                        />
                                    </PeripheryDetailTitle>
                                    <Box>
                                        <Box sx={{ fontWeight: 500 }}>
                                            {Formatter.dayMonthYearHourMinute(
                                                DateTime.fromISO(
                                                    runningEntryWithoutExit.enteredAt,
                                                ),
                                            )}
                                        </Box>
                                        <Box sx={{ fontSize: '15px' }}>
                                            <Localized
                                                de="Einfahrt mit "
                                                fr="entrée avec "
                                                it="entrata con "
                                                en="entry with "
                                            />
                                            <IdentificationMediaTranslaction
                                                identificationMedia={
                                                    runningEntryWithoutExit.identificationMedia
                                                }
                                            />
                                            {runningEntryWithoutExit.paymentChannel !==
                                                PaymentChannel.PAY_STATION && (
                                                <>
                                                    {' ('}
                                                    <PaymentChannelLocalized
                                                        paymentChannel={
                                                            runningEntryWithoutExit.paymentChannel
                                                        }
                                                    />
                                                    {')'}
                                                </>
                                            )}
                                        </Box>
                                        {runningEntryWithoutExit.payStationPaymentsInfo !==
                                            null && (
                                            <PayStationPaymentInfoDisplay
                                                payStationPaymentsInfo={
                                                    runningEntryWithoutExit.payStationPaymentsInfo
                                                }
                                            />
                                        )}
                                        <AmountToBePaidDisplay
                                            amountToBePaid={
                                                runningEntryWithoutExit.amountToBePaid
                                            }
                                            duration={
                                                runningEntryWithoutExit.duration
                                            }
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                        {presence?.resolvedEntryWithoutExits &&
                            presence.resolvedEntryWithoutExits.length > 0 && (
                                <Box
                                    sx={{
                                        paddingTop: theme => theme.spacing(2),
                                        color: theme => theme.palette.grey.A700,
                                    }}
                                >
                                    <PeripheryDetailTitle>
                                        <Localized
                                            de="Frühere Transaktionen"
                                            fr="Transactions précédentes"
                                            it="Transazioni precedenti"
                                            en="Previous transactions"
                                        />
                                    </PeripheryDetailTitle>
                                    {presence.resolvedEntryWithoutExits.map(
                                        transaction => (
                                            <PeripheryGrid
                                                key={
                                                    transaction.entryWithoutExitId
                                                }
                                                topLeft={
                                                    <>
                                                        {Formatter.dayMonthYear(
                                                            DateTime.fromISO(
                                                                transaction.resolvedAt,
                                                            ),
                                                        )}
                                                        {` (${transaction.duration})`}
                                                    </>
                                                }
                                                bottomLeft={
                                                    <>
                                                        <IdentificationMediaTranslaction
                                                            identificationMedia={
                                                                transaction.identificationMedia
                                                            }
                                                        />
                                                        {' ('}
                                                        <PaymentChannelLocalized
                                                            paymentChannel={
                                                                transaction.paymentChannel
                                                            }
                                                        />
                                                        {')'}
                                                    </>
                                                }
                                                topRight={
                                                    <EntryResolvedTranslation
                                                        resolvedReason={
                                                            transaction.resolvedReason
                                                        }
                                                    />
                                                }
                                                bottomRight={undefined}
                                            />
                                        ),
                                    )}
                                </Box>
                            )}
                    </Box>

                    {!barrierPermanentlyOpen && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                paddingTop: theme => theme.spacing(2),
                            }}
                        >
                            <RequestErrorAlert
                                open={
                                    peripheryCommandRequestState.status ===
                                    RequestStatus.ERROR
                                }
                            />
                            <ParkingPortalButton
                                onClick={() =>
                                    executePeripheryCommand({
                                        operatorId: currentLogin.mandantId,
                                        zoneId: peripheryConfig.zoneId,
                                        peripheryType:
                                            peripheryConfig.peripheryType,
                                        onPremiseId:
                                            peripheryConfig.onPremiseId,
                                        commandType:
                                            CloudConnectorCommandType.BARRIER_UP_ONCE,
                                        secondaryGate: false,
                                        permanentlyOpen: null,
                                        forceOutOfOrder: null,
                                        zoneBarrierGateEntryWithoutExitId:
                                            presence?.runningEntryWithoutExit
                                                ?.entryWithoutExitId ?? null,
                                    })
                                }
                                disabled={
                                    peripheryCommandRequestState.status ===
                                    RequestStatus.PENDING
                                }
                                icon={
                                    presence?.runningEntryWithoutExit
                                        ?.entryWithoutExitId ? (
                                        <KeyboardDoubleArrowRight />
                                    ) : (
                                        <KeyboardControlKey />
                                    )
                                }
                            >
                                {isExitPeriphery &&
                                presence?.runningEntryWithoutExit
                                    ?.entryWithoutExitId ? (
                                    <Localized
                                        de="Manuelle Ausfahrt"
                                        fr="Sortie manuelle"
                                        it="Uscita manuale"
                                        en="Manual exit"
                                    />
                                ) : (
                                    <Localized
                                        de="Barriere öffnen (1x)"
                                        fr="Ouvrir la barrière (1x)"
                                        it="Apri barriera (1x)"
                                        en="Open barrier (1x)"
                                    />
                                )}
                            </ParkingPortalButton>
                        </Box>
                    )}
                </Box>
            </Box>
        </Stack>
    );
}

function SpecialVehicleIcon({
    specialVehicleType,
}: {
    specialVehicleType: SpecialVehicleType;
}) {
    switch (specialVehicleType) {
        case SpecialVehicleType.HEIGHT:
            return <DirectionsBus sx={{ fontSize: '28px' }} />;
        case SpecialVehicleType.MOTORCYCLE:
            return <TwoWheeler sx={{ fontSize: '28px' }} />;
    }
}

function PayStationPaymentInfoDisplay({
    payStationPaymentsInfo,
}: {
    payStationPaymentsInfo: PayStationPaymentsInfo;
}) {
    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    paddingTop: theme => theme.spacing(2),
                    fontWeight: 500,
                }}
            >
                <Box>
                    {Formatter.dayMonthYearHourMinute(
                        DateTime.fromISO(payStationPaymentsInfo.lastVisitedAt),
                    )}
                </Box>

                <Box>{`CHF ${payStationPaymentsInfo.amount.toFixed(2)}`}</Box>
            </Box>

            <PayStationPaymentInfoStateTranslation
                payStationPaymentInfoState={payStationPaymentsInfo.state}
            />
        </Box>
    );
}

function AmountToBePaidDisplay({
    amountToBePaid,
    duration,
}: {
    amountToBePaid: number;
    duration: string;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                paddingTop: theme => theme.spacing(2),
                fontWeight: 500,
                color: theme =>
                    amountToBePaid > 0
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
            }}
        >
            <Box>
                <Localized
                    de={`Offener Betrag (${duration})`}
                    fr={`Montant ouvert (${duration})`}
                    it={`Importo aperto (${duration})`}
                    en={`Open amount (${duration})`}
                />
            </Box>

            <Box>{`CHF ${amountToBePaid.toFixed(2)}`}</Box>
        </Box>
    );
}

function PayStationPaymentInfoStateTranslation({
    payStationPaymentInfoState,
}: {
    payStationPaymentInfoState: PayStationPaymentInfoState;
}) {
    switch (payStationPaymentInfoState) {
        case PayStationPaymentInfoState.PAID:
            return (
                <Localized
                    de="Kassezahlung(en)"
                    fr="Paiement(s) caisse"
                    it="Pagamento/i cassa"
                    en="Pay station payment(s)"
                />
            );
        case PayStationPaymentInfoState.QUOTED:
            return (
                <Localized
                    de="Kasse (ohne Zahlung)"
                    fr="Caisse (sans paiement)"
                    it="Cassa (senza pagamento)"
                    en="Pay station (no payment)"
                />
            );
    }
}

function IdentificationMediaTranslaction({
    identificationMedia,
}: {
    identificationMedia: IdentificationMedia;
}): JSX.Element {
    switch (identificationMedia) {
        case IdentificationMedia.BADGE:
            return <Localized de="Badge" fr="badge" it="badge" en="badge" />;
        case IdentificationMedia.LICENSE_PLATE:
            return (
                <Localized
                    de="Kennzeichen"
                    fr="immatriculation"
                    it="numero di targa"
                    en="license plate"
                />
            );
        case IdentificationMedia.QR_CODE:
            return (
                <Localized
                    de="QR-Code"
                    fr="code QR"
                    it="codice QR"
                    en="QR code"
                />
            );
        case IdentificationMedia.TICKET:
            return <>Ticket</>;
        case IdentificationMedia.SPACE:
            return <></>;
    }
}

function EntryResolvedTranslation({
    resolvedReason,
}: {
    resolvedReason: EntryResolvedReason;
}): JSX.Element {
    switch (resolvedReason) {
        case EntryResolvedReason.regular_exit:
            return (
                <Localized
                    de="normale Ausfahrt"
                    fr="Sortie normale"
                    it="uscita normale"
                    en="regular exit"
                />
            );
        case EntryResolvedReason.cleared_by_operator:
            return (
                <Localized
                    de="annulliert (manuell)"
                    fr="annulée (manuellement)"
                    it="annullata (manualmente)"
                    en="cancelled (manually)"
                />
            );
        case EntryResolvedReason.barrier_opened_by_operator:
            return (
                <Localized
                    de="manuelle Ausfahrt"
                    fr="sortie manuelle"
                    it="uscita manuale"
                    en="manual exit"
                />
            );
        case EntryResolvedReason.overwritten_by_entry:
            return (
                <Localized
                    de="annulliert (durch neue Einfahrt)"
                    fr="annulée (par nouvelle entrée)"
                    it="annullata (da nuova entrata)"
                    en="cancelled (by new entry)"
                />
            );
        case EntryResolvedReason.canceled_by_equipment:
        case EntryResolvedReason.cleared_by_system:
            return <>{EmDash}</>;
    }
}

function PresenceNotOkMessage({
    peripheryType,
}: {
    peripheryType: CloudConnectorPeripheryType;
}): JSX.Element {
    switch (peripheryType) {
        case CloudConnectorPeripheryType.ENTRY:
            return (
                <Localized
                    de="Einfahrt nicht OK"
                    fr="Entrée pas OK"
                    it="Entrata non OK"
                    en="Drive-in not OK"
                />
            );
        case CloudConnectorPeripheryType.EXIT:
            return (
                <Localized
                    de="Ausfahrt nicht OK"
                    fr="Sortie pas OK"
                    it="Uscita non OK"
                    en="Exit not OK"
                />
            );
        case CloudConnectorPeripheryType.TRANSIT_ENTRY:
            return (
                <Localized
                    de="Durchfahrt nicht OK"
                    fr="Transit pas OK"
                    it="Transito non OK"
                    en="Transit not OK"
                />
            );
        case CloudConnectorPeripheryType.ACCESS:
        case CloudConnectorPeripheryType.PAY_STATION:
            return <></>;
    }
}

function PresenceOkMessage({
    peripheryType,
}: {
    peripheryType: CloudConnectorPeripheryType;
}): JSX.Element {
    switch (peripheryType) {
        case CloudConnectorPeripheryType.ENTRY:
            return (
                <Localized
                    de="Einfahrt OK"
                    fr="Entrée OK"
                    it="Entrata OK"
                    en="Entry OK"
                />
            );
        case CloudConnectorPeripheryType.EXIT:
            return (
                <Localized
                    de="Ausfahrt OK"
                    fr="Sortie OK"
                    it="Uscita OK"
                    en="Exit OK"
                />
            );
        case CloudConnectorPeripheryType.TRANSIT_ENTRY:
            return (
                <Localized
                    de="Durchfahrt OK"
                    fr="Transit OK"
                    it="Transito OK"
                    en="Transit OK"
                />
            );
        case CloudConnectorPeripheryType.ACCESS:
        case CloudConnectorPeripheryType.PAY_STATION:
            return <></>;
    }
}

export function PeripheryDetailSeparator({
    noMarginBottom,
}: {
    noMarginBottom?: boolean;
}): JSX.Element {
    return (
        <Box
            sx={{
                marginTop: theme => theme.spacing(2),
                marginBottom: noMarginBottom
                    ? undefined
                    : theme => theme.spacing(2),
                borderBottom: 'solid 1px black',
            }}
        />
    );
}

export function PeripheryDetailTitle({
    children,
    primary,
}: {
    children: ReactNode;
    primary?: boolean;
}) {
    return (
        <Box
            sx={{
                fontWeight: 700,
                textAlign: 'center',
                textTransform: 'uppercase',
                paddingBottom: '4px',
                color: theme =>
                    primary
                        ? theme.palette.primary.main
                        : theme.palette.grey.A700,
            }}
        >
            {children}
        </Box>
    );
}

export function PeripheryGrid({
    topLeft,
    bottomLeft,
    topRight,
    bottomRight,
    primary,
    noPadding,
}: {
    topLeft: ReactNode;
    bottomLeft: ReactNode;
    topRight: ReactNode;
    bottomRight: ReactNode;
    primary?: boolean;
    noPadding?: boolean;
}) {
    return (
        <Box
            sx={{
                paddingBottom: noPadding
                    ? undefined
                    : theme => theme.spacing(2),
                color: theme =>
                    primary
                        ? theme.palette.primary.main
                        : theme.palette.grey.A700,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    fontWeight: 500,
                }}
            >
                <Box>{topLeft}</Box>
                <Box>{topRight}</Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    fontSize: '14px',
                }}
            >
                <Box>{bottomLeft}</Box>
                <Box>{bottomRight}</Box>
            </Box>
        </Box>
    );
}
